import React, { Ref, DetailedHTMLProps, InputHTMLAttributes } from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'

export const textBoxBorderRule = `1px solid ${theme.color.neutral50}`

export const textBoxBorderStyle = css`
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  border-radius: 3px;
  border: ${textBoxBorderRule};
`

export const textBoxFocusedBorderStyle = css`
  border: 1px solid ${theme.color.turquoise70};
  box-shadow: 0 0 1px ${theme.color.turquoise70};
`

export const textBoxBaseStyle = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-weight: ${deprecatedTheme.fontWeights.secondary.regular};
  font-size: 14px;
  color: ${theme.color.neutral100};
  background-color: ${theme.color.white};
  display: block;
  padding: ${deprecatedTheme.padding.comboboxInput};
  width: 100%;
  min-height: 34px;
  height: 34px;
  outline: 0;
  ${textBoxBorderStyle};
`

const errorColor = theme.color.red50

const textBoxInvalidBorderStyle = css`
  color: ${errorColor};
  border: 1px solid ${errorColor};
`

const textBoxStyle = css`
  ${textBoxBaseStyle};

  &:focus {
    ${textBoxFocusedBorderStyle};
  }

  &::placeholder {
    opacity: 0.3;
  }
`

const textBoxInvalidStyle = cx(
  textBoxStyle,
  css`
    ${textBoxInvalidBorderStyle};

    &:focus {
      border: 1px solid ${errorColor};
      box-shadow: 0 0 1px ${errorColor};
    }
  `
)

export const textBoxReadOnlyStyle = cx(
  textBoxStyle,
  css`
    font-family: ${deprecatedTheme.fontFamilies.secondary};
    font-weight: ${deprecatedTheme.fontWeights.secondary.regular};
    font-size: 14px;
    color: ${theme.color.neutral60};

    border: 1px solid ${theme.color.neutral40};
    background-color: ${theme.color.neutral20};

    &:focus {
      border: 1px solid ${theme.color.neutral50};
      box-shadow: 0 0 1px ${theme.color.neutral60};
    }
  `
)

export interface TextBoxProps
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  readonly error?: boolean
  readonly inputRef?: Ref<HTMLInputElement>
}

export function TextBox({
  className: customClassName,
  disabled,
  error,
  inputRef,
  readOnly,
  ...props
}: TextBoxProps) {
  const enabledStyle = error ? textBoxInvalidStyle : textBoxStyle

  const className = cx(
    disabled || readOnly ? textBoxReadOnlyStyle : enabledStyle,
    customClassName
  )

  return (
    <input
      aria-invalid={error ? true : undefined}
      className={cx('legacy-ds-textbox', className)}
      disabled={disabled}
      readOnly={readOnly}
      ref={inputRef}
      {...props}
    />
  )
}
